/**
 * Utilities for copying chat message content (rich styled HTML or clean plain text / stripped markdown).
 */

export const fallbackCopyText = (text: string): boolean => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  try {
    const ok = document.execCommand('copy');
    return ok;
  } catch (err) {
    console.error('fallbackCopyText failed:', err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

export const fallbackCopyHtml = async (html: string, text: string): Promise<boolean> => {
  let success = false;
  const handler = (e: ClipboardEvent) => {
    try {
      if (!e.clipboardData) return;
      e.clipboardData.setData('text/html', html);
      e.clipboardData.setData('text/plain', text);
      e.preventDefault();
      success = true;
    } catch {
      // ignore
    }
  };

  document.addEventListener('copy', handler);
  try {
    const ok = document.execCommand('copy');
    if (!ok && !success) {
      return fallbackCopyText(text);
    }
    return true;
  } catch {
    return fallbackCopyText(text);
  } finally {
    document.removeEventListener('copy', handler);
  }
};

export const copyRawText = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('navigator.clipboard.writeText failed, using fallback:', err);
    }
  }
  return fallbackCopyText(text);
};

/**
 * Cleans up redundant blank lines and whitespace.
 */
export const cleanBlankLines = (text: string): string => {
  if (!text) return '';

  return text
    // Normalize newlines
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Trim trailing whitespace from each line
    .replace(/[ \t]+$/gm, '')
    // Convert lines containing only whitespace into completely empty lines
    .replace(/^[ \t]+$/gm, '')
    // Remove extra blank line between consecutive bullet items
    .replace(/(^[ \t]*[•*-]\s+[^\n]+)\n{2,}(^[ \t]*[•*-]\s+)/gm, '$1\n$2')
    // Remove extra blank line between consecutive numbered items
    .replace(/(^[ \t]*\d+[.)]\s+[^\n]+)\n{2,}(^[ \t]*\d+[.)]\s+)/gm, '$1\n$2')
    // Remove extra blank line between consecutive checkbox items
    .replace(/(^[ \t]*[☑☐]\s+[^\n]+)\n{2,}(^[ \t]*[☑☐]\s+)/gm, '$1\n$2')
    // Collapse 3 or more consecutive newlines into at most 2 (single empty line between paragraphs)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Strips markdown syntax and returns clean plain text without excessive blank lines.
 */
export const stripMarkdown = (markdown: string): string => {
  if (!markdown) return '';

  let text = markdown;

  // Normalize newlines
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Strip code blocks: keep only inner code without fences
  text = text.replace(/```[^\n]*\n([\s\S]*?)```/g, '$1\n');

  // Strip inline code backticks: `code` -> code
  text = text.replace(/`([^`]+)`/g, '$1');

  // Strip HTML tags: <tag> -> ''
  text = text.replace(/<[^>]*>/g, '');

  // Strip images: ![alt](url) -> alt
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');

  // Strip links: [text](url) -> text
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');

  // Strip headings: # Heading -> Heading
  text = text.replace(/^#{1,6}\s+(.*)$/gm, '$1');

  // Strip blockquotes: > quote -> quote
  text = text.replace(/^>\s+(.*)$/gm, '$1');

  // Strip bold and italic formatting:
  // ***bold italic*** or ___bold italic___
  text = text.replace(/(\*{3}|_{3})(.*?)\1/g, '$2');
  // **bold** or __bold__
  text = text.replace(/(\*{2}|_{2})(.*?)\1/g, '$2');
  // *italic* or _italic_
  text = text.replace(/(\*|_)(.*?)\1/g, '$2');
  // ~~strikethrough~~
  text = text.replace(/~~(.*?)~~/g, '$1');

  // Strip table delimiters and formatting:
  // Remove table separator lines like |---|---|
  text = text.replace(/^\|?[\s-:]+\|[\s\-:|]*$/gm, '');
  // Format table rows by replacing pipes with spaces
  text = text.replace(/^\|\s*(.*?)\s*\|$/gm, '$1');
  text = text.replace(/\s*\|\s*/g, ' | ');

  // Strip horizontal rules
  text = text.replace(/^[-*_]{3,}\s*$/gm, '');

  // Clean task list checkboxes: [ ] or [x]
  text = text.replace(/^[\t ]*[-*+]\s+\[ \]\s*/gm, '☐ ');
  text = text.replace(/^[\t ]*[-*+]\s+\[[xX]\]\s*/gm, '☑ ');

  // Clean bullet list markers
  text = text.replace(/^[\t ]*[-*+]\s+/gm, '• ');

  return cleanBlankLines(text);
};

/**
 * Extracts plain text without markdown from rendered DOM element or fallback markdown string.
 */
export const extractPlainText = (element: HTMLElement | null, fallbackMarkdown: string): string => {
  if (element) {
    try {
      const clone = element.cloneNode(true) as HTMLElement;

      // Remove non-content interactive buttons/loaders
      const unwanted = clone.querySelectorAll('button, svg, [role="button"]');
      unwanted.forEach((el) => el.remove());

      const rawInnerText = clone.innerText;
      if (rawInnerText && rawInnerText.trim()) {
        return cleanBlankLines(rawInnerText);
      }
    } catch (e) {
      console.warn('Failed to extract innerText from DOM element:', e);
    }
  }

  return stripMarkdown(fallbackMarkdown);
};

export const copyPlainTextWithoutMarkdown = async (
  element: HTMLElement | null,
  fallbackMarkdown: string
): Promise<boolean> => {
  const plainText = extractPlainText(element, fallbackMarkdown);
  return copyRawText(plainText);
};

export const copyHtmlWithStyling = async (
  element: HTMLElement | null,
  fallbackText: string
): Promise<boolean> => {
  if (!element) {
    return copyRawText(fallbackText);
  }

  try {
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    document.body.appendChild(tempContainer);

    const clonedElement = element.cloneNode(true) as HTMLElement;
    tempContainer.appendChild(clonedElement);

    // Remove buttons or interactive control elements if any inside cloned element
    const buttons = clonedElement.querySelectorAll('button');
    buttons.forEach((btn) => btn.remove());

    const allElements = clonedElement.querySelectorAll('*');
    const originalElements = element.querySelectorAll('*');

    const stylesToCopy = [
      'color',
      'background-color',
      'font-family',
      'font-size',
      'font-weight',
      'font-style',
      'text-decoration',
      'text-align',
      'line-height',
      'margin-top',
      'margin-bottom',
      'margin-left',
      'margin-right',
      'padding-top',
      'padding-bottom',
      'padding-left',
      'padding-right',
      'border',
      'border-radius',
      'list-style-type',
      'display',
    ];

    allElements.forEach((el, index) => {
      const origEl = originalElements[index] as HTMLElement;
      if (origEl) {
        const computedStyle = window.getComputedStyle(origEl);
        const inlineStyle: string[] = [];

        stylesToCopy.forEach((prop) => {
          const value = computedStyle.getPropertyValue(prop);
          if (value && value !== 'none' && value !== 'normal' && value !== 'auto') {
            inlineStyle.push(`${prop}: ${value}`);
          }
        });

        if (inlineStyle.length > 0) {
          (el as HTMLElement).setAttribute('style', inlineStyle.join('; '));
        }
      }
    });

    const rootComputedStyle = window.getComputedStyle(element);
    const rootInlineStyle: string[] = [];
    ['color', 'background-color', 'font-family', 'font-size', 'line-height'].forEach((prop) => {
      const value = rootComputedStyle.getPropertyValue(prop);
      if (value) {
        rootInlineStyle.push(`${prop}: ${value}`);
      }
    });
    clonedElement.setAttribute('style', rootInlineStyle.join('; '));

    const checkboxes = clonedElement.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      const isChecked = (checkbox as HTMLInputElement).checked;
      const symbol = document.createTextNode(isChecked ? '☑ ' : '☐ ');
      checkbox.parentNode?.replaceChild(symbol, checkbox);
    });

    const headings = clonedElement.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading) => {
      const nextSibling = heading.nextElementSibling;
      if (nextSibling && nextSibling.tagName === 'P') {
        const spacer = document.createElement('br');
        if (nextSibling.nextSibling) {
          nextSibling.parentNode?.insertBefore(spacer, nextSibling.nextSibling);
        } else {
          nextSibling.parentNode?.appendChild(spacer);
        }
      }
    });

    const styledHtml = clonedElement.outerHTML;
    const textContent = fallbackText || element.textContent || '';

    document.body.removeChild(tempContainer);

    const canWriteRich =
      typeof window !== 'undefined' &&
      'ClipboardItem' in window &&
      typeof navigator !== 'undefined' &&
      !!navigator.clipboard?.write;

    if (canWriteRich) {
      const clipboardItem = new (window as any).ClipboardItem({
        'text/html': new Blob([styledHtml], { type: 'text/html' }),
        'text/plain': new Blob([textContent], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([clipboardItem]);
      return true;
    } else {
      return await fallbackCopyHtml(styledHtml, textContent);
    }
  } catch (err) {
    console.error('Failed to copy with styling:', err);
    return copyRawText(fallbackText);
  }
};
